import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const { login } = useAuth();
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (process.env.REACT_APP_DEBUG_FORM_SUBMISSION === 'true') {
      console.log('=== LOGIN FORM SUBMISSION DEBUG ===');
      console.log('Form submitted with email:', loginForm.email);
      console.log('Password provided:', !!loginForm.password);
      console.log('Current login loading state:', loginLoading);
      console.log('Current login error state:', loginError);
    }

    setLoginLoading(true);
    setLoginError('');
    if (process.env.REACT_APP_DEBUG_FORM_SUBMISSION === 'true') console.log('Set loading to true, cleared error');

    try {
      if (process.env.REACT_APP_DEBUG_API_PREFETCH === 'true') console.log('Calling login from AuthContext...');
      const response = await login(loginForm.email, loginForm.password);
      if (process.env.REACT_APP_DEBUG_API_RESPONSE === 'true') {
        console.log('Login response in login component received:', response);
        console.log('Response success:', response?.success);
      }
      if (response.success) {
        if (process.env.REACT_APP_DEBUG_API_RESPONSE === 'true') {
          console.log('Login successful! User data:', response.user);
        }

        if (process.env.REACT_APP_DEBUG_COMPONENT_LOAD === 'true') console.log('Clearing form...');
        setLoginForm({ email: '', password: '' });
        if (process.env.REACT_APP_DEBUG_COMPONENT_LOAD === 'true') console.log('Form cleared, login handled by AuthContext');
      } else {
        console.warn('Login failed with response:', response);
        const errorMsg = response.error || 'Login failed';
        console.log('Setting error message:', errorMsg);
        setLoginError(errorMsg);
      }
    } catch (error) {
      console.error('=== LOGIN CATCH BLOCK DEBUG ===');
      console.error('Caught error during login:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      const errorMsg = error.message || 'Login failed';
      console.log('Setting error message from catch:', errorMsg);
      setLoginError(errorMsg);
      console.log('=== END LOGIN CATCH BLOCK DEBUG ===');
    } finally {
      // console.log('Setting loading to false');
      setLoginLoading(false);
      // console.log('=== END LOGIN FORM SUBMISSION DEBUG ===');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="login-page min-vh-100 d-flex align-items-center bg-light">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            <Card className="shadow">
              <Card.Header className="bg-primary text-white text-center py-4">
                <div className="mb-3">
                  <img src="/logo_current.png" alt="Company Logo" style={{ height: '80px', maxWidth: '200px', objectFit: 'contain' }} />
                </div>
                <h2 className="mb-0">
                  {process.env.REACT_APP_TITLE}
                </h2>
                <p className="mb-0 opacity-75">{process.env.REACT_APP_COMPANY_NAME}</p>
              </Card.Header>
              {process.env.REACT_APP_ENVIRONMENT !== 'production' && process.env.REACT_APP_ENVIRONMENT_WARNING && (
                <Alert variant={process.env.REACT_APP_ENVIRONMENT_WARNING_VARIANT || "warning"} className="mb-0 border-0 rounded-0 text-center">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {process.env.REACT_APP_ENVIRONMENT_WARNING}
                </Alert>
              )}
              <Card.Body className="p-4">
                <div className="text-center mb-4">
                  <h4>
                    <i className="bi bi-shield-lock me-2"></i>
                    Sign In
                  </h4>
                  <p className="text-muted">Access the enterprise environment</p>
                </div>

                {loginError && (
                  <Alert variant="danger" className="mb-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {loginError}
                  </Alert>
                )}

                <Form onSubmit={handleLogin}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <i className="bi bi-envelope me-2"></i>
                      Email Address
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={loginForm.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                      disabled={loginLoading}
                      size="lg"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>
                      <i className="bi bi-lock me-2"></i>
                      Password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={loginForm.password}
                      onChange={handleInputChange}
                      placeholder="Enter your password"
                      required
                      disabled={loginLoading}
                      size="lg"
                    />
                  </Form.Group>

                  <div className="d-grid mb-3">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loginLoading}
                      size="lg"
                    >
                      {loginLoading ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Signing In...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-box-arrow-in-right me-2"></i>
                          Sign In
                        </>
                      )}
                    </Button>
                  </div>
                </Form>

                <hr className="my-4" />

                <div className="text-center text-muted small">
                  <p className="mb-1">Need help accessing your account?</p>
                  <a href="#" className="text-decoration-none">
                    Contact your administrator
                  </a>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
